<script setup>
import { ref, onMounted } from "vue";
import { useInscripcionTours } from "@/store/inscripcionTours";
import vSelect from "vue-select";
import avatarPorDefecto from "@/assets/img/Evolve/usuario.png";
import { saveToIndexedDB, getFromIndexedDB } from "@/utils/indexedDB";
import Swal from "sweetalert2";

import youtubeIcon from "@/assets/img/Evolve/youtube.png";
import facebookIcon from "@/assets/img/Evolve/facebook.png";
import instagramIcon from "@/assets/img/Evolve/instagram.png";
import twitterIcon from "@/assets/img/Evolve/twitter.png";

const emit = defineEmits(["next-step"]);
const store = useInscripcionTours();

const imagePreview = ref(null);
const imageError = ref(null);
const errors = ref({});
const imageInput = ref(null);

const MAX_SIZE_MB = 5;
const allowedExt = ["jpg", "jpeg", "png"];

const opcionesNacionalidades = [
  { nombre: "México", codigo_iso: "mx" },
  { nombre: "Estados Unidos", codigo_iso: "us" },
  { nombre: "Canadá", codigo_iso: "ca" },
  { nombre: "Brasil", codigo_iso: "br" },
  { nombre: "Argentina", codigo_iso: "ar" },
  { nombre: "España", codigo_iso: "es" },
  { nombre: "Francia", codigo_iso: "fr" },
  { nombre: "Alemania", codigo_iso: "de" },
  { nombre: "Italia", codigo_iso: "it" },
  { nombre: "Japón", codigo_iso: "jp" },
];

const iconosRedes = {
  instagram: instagramIcon,
  facebook: facebookIcon,
  youtube: youtubeIcon,
  twitter: twitterIcon,
};

const placeholderRedes = {
  instagram: "https://instagram.com/tu_usuario",
  facebook: "https://facebook.com/tu_usuario",
  youtube: "https://youtube.com/@tu_canal",
  twitter: "https://twitter.com/tu_usuario",
};

onMounted(async () => {
  const file = await getFromIndexedDB("peleadores", "fotoPerfil");
  if (file) {
    imagePreview.value = URL.createObjectURL(file);
    store.imagenPerfil = imagePreview.value;
    return;
  }
  if (store.imagenPerfil) {
    imagePreview.value = store.imagenPerfil;
  }
});

function formatearTelefono(numero) {
  const limpio = numero.replace(/\D/g, "");
  if (limpio.length === 10) return `+52${limpio}`;
  if (limpio.length === 12 && limpio.startsWith("52")) return `+${limpio}`;
  if (limpio.length === 13 && limpio.startsWith("521")) return `+${limpio}`;
  return null;
}

function validarCampos() {
  errors.value = {};
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const telefonoFormateado = formatearTelefono(store.telefono);

  if (!store.nombre.trim()) errors.value.nombre = true;
  if (!store.apellido.trim()) errors.value.apellido = true;
  if (!store.nacionalidad) errors.value.nacionalidad = true;
  if (!emailRegex.test(store.email)) errors.value.email = true;
  if (!telefonoFormateado) errors.value.telefono = true;
  else store.telefono = telefonoFormateado;
  if (!store.direccion.trim()) errors.value.direccion = true;

  return Object.keys(errors.value).length === 0;
}

function continuar() {
  if (!store.imagenPerfil) {
    Swal.fire({
      icon: "warning",
      title: "Falta la foto",
      text: "¡No olvides tu foto de perfil! Es parte de tu presentación para el torneo.",
      confirmButtonText: "Entendido",
      confirmButtonColor: "#007bff",
    });
    return;
  }
  if (validarCampos()) emit("next-step");
}

function triggerImageUpload() {
  imageInput.value?.click();
}

async function handleProfileImageChange(event) {
  imageError.value = null;
  const file = event.target.files[0];
  if (!file) {
    store.imagenPerfil = "";
    imagePreview.value = null;
    return;
  }

  const ext = file.name.split(".").pop().toLowerCase();
  const extOk = allowedExt.includes(ext);
  const mimeOk = file.type.startsWith("image/");
  const sizeOk = file.size <= MAX_SIZE_MB * 1024 * 1024;

  if (!extOk || !mimeOk) imageError.value = "Formato no válido. Usa JPG o PNG.";
  else if (!sizeOk) imageError.value = `La imagen supera ${MAX_SIZE_MB} MB.`;

  if (imageError.value) {
    store.imagenPerfil = "";
    imagePreview.value = null;
    event.target.value = "";
    return;
  }

  await saveToIndexedDB("peleadores", "fotoPerfil", file);
  imagePreview.value = URL.createObjectURL(file);
  store.imagenPerfil = imagePreview.value;
}
</script>

<template>
  <form class="step-container" @submit.prevent="continuar">
    <!-- ── Bloque foto + campos ── -->
    <div class="foto-campos-row">
      <!-- Foto cuadrada con bordes redondeados -->
      <div class="foto-col">
        <div class="foto-cuadro" @click="triggerImageUpload">
          <img
            :src="imagePreview || store.imagenPerfil || avatarPorDefecto"
            alt="Foto de perfil"
            class="foto-img"
          />
          <div class="foto-overlay">
            <i class="bi bi-camera-fill"></i>
            <span>Cambiar foto</span>
          </div>
        </div>
        <input
          ref="imageInput"
          type="file"
          class="d-none"
          accept=".jpg,.jpeg,.png"
          @change="handleProfileImageChange"
        />
        <p v-if="imageError" class="foto-error">{{ imageError }}</p>
        <p class="foto-hint">JPG o PNG · máx. 5 MB</p>
      </div>

      <!-- Campos a la derecha -->
      <div class="campos-col">
        <!-- Nombre + Apellido -->
        <div class="form-row-2">
          <div class="field-group">
            <label class="field-label">Nombre <span class="req">*</span></label>
            <input
              type="text"
              class="field-input"
              :class="{ 'field-error': errors.nombre }"
              v-model="store.nombre"
              autocomplete="given-name"
              placeholder="Ej. Carlos"
            />
            <span v-if="errors.nombre" class="error-msg">Campo requerido</span>
          </div>
          <div class="field-group">
            <label class="field-label">Apellido <span class="req">*</span></label>
            <input
              type="text"
              class="field-input"
              :class="{ 'field-error': errors.apellido }"
              v-model="store.apellido"
              autocomplete="family-name"
              placeholder="Ej. García"
            />
            <span v-if="errors.apellido" class="error-msg">Campo requerido</span>
          </div>
        </div>

        <!-- Email + Teléfono -->
        <div class="form-row-2">
          <div class="field-group">
            <label class="field-label"
              >Correo electrónico <span class="req">*</span></label
            >
            <input
              type="email"
              class="field-input"
              :class="{ 'field-error': errors.email }"
              v-model="store.email"
              autocomplete="email"
              placeholder="correo@ejemplo.com"
            />
            <span v-if="errors.email" class="error-msg">Correo no válido</span>
          </div>
          <div class="field-group">
            <label class="field-label">Teléfono <span class="req">*</span></label>
            <input
              type="tel"
              class="field-input"
              :class="{ 'field-error': errors.telefono }"
              v-model="store.telefono"
              autocomplete="tel"
              placeholder="10 dígitos"
            />
            <span v-if="errors.telefono" class="error-msg">Teléfono no válido</span>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Nacionalidad + Dirección (50/50) ── -->
    <div class="form-row-2">
      <div class="field-group">
        <label class="field-label">Nacionalidad <span class="req">*</span></label>
        <v-select
          v-model="store.nacionalidad"
          :options="opcionesNacionalidades"
          label="nombre"
          :reduce="(n) => n.codigo_iso"
          :clearable="false"
          placeholder="Selecciona tu país"
          class="custom-select"
          :class="{ 'select-error': errors.nacionalidad }"
        />
        <span v-if="errors.nacionalidad" class="error-msg">Campo requerido</span>
      </div>
      <div class="field-group">
        <label class="field-label">Dirección <span class="req">*</span></label>
        <input
          type="text"
          class="field-input"
          :class="{ 'field-error': errors.direccion }"
          v-model="store.direccion"
          placeholder="Calle, Col., Ciudad, Estado"
        />
        <span v-if="errors.direccion" class="error-msg">Campo requerido</span>
      </div>
    </div>

    <!-- ── Redes sociales ── -->
    <div class="redes-section">
      <p class="redes-titulo">
        Redes sociales
        <span style="font-size: 0.78rem; font-weight: 400; color: #aaa"> (opcional)</span>
      </p>
      <div class="redes-grid">
        <div v-for="red in Object.keys(iconosRedes)" :key="red" class="red-field">
          <span class="red-icon">
            <img :src="iconosRedes[red]" :alt="red" />
          </span>
          <input
            type="url"
            class="field-input red-input"
            v-model="store.redesSociales[red]"
            :placeholder="placeholderRedes[red]"
          />
        </div>
      </div>
    </div>

    <!-- ── Navegación ── -->
    <div class="nav-row">
      <a href="/" class="btn-nav btn-secondary-nav">
        <i class="bi bi-house-fill"></i>
        <span>Inicio</span>
      </a>
      <button type="submit" class="btn-nav btn-primary-nav">
        <span>Siguiente</span>
        <i class="bi bi-arrow-right"></i>
      </button>
    </div>
  </form>
</template>

<style scoped>
.step-container {
  padding: 0.5rem 0;
}

/* Título */
.step-title {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 1.5rem;
}
.step-number {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #007bff;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1rem;
  flex-shrink: 0;
}
.step-title h5 {
  font-weight: 700;
  color: #1a1a2e;
  font-size: 1.05rem;
  margin: 0;
}
.step-sub {
  margin: 0;
  font-size: 0.78rem;
  color: #888;
}

/* ── Bloque foto + campos ── */
.foto-campos-row {
  display: grid;
  grid-template-columns: 160px 1fr;
  gap: 24px;
  margin-bottom: 16px;
  align-items: start;
}

/* Foto cuadrada */
.foto-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}
.foto-cuadro {
  width: 150px;
  height: 150px;
  border-radius: 14px;
  border: 2px solid #007bff;
  overflow: hidden;
  position: relative;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(0, 123, 255, 0.18);
  transition: box-shadow 0.25s ease, border-color 0.25s ease;
}
.foto-cuadro:hover {
  box-shadow: 0 4px 22px rgba(0, 123, 255, 0.35);
  border-color: #0060df;
}
.foto-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
  display: block;
}
.foto-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.48);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  opacity: 0;
  transition: opacity 0.2s ease;
  color: #fff;
  font-size: 1.3rem;
}
.foto-overlay span {
  font-size: 0.72rem;
  letter-spacing: 0.5px;
  font-weight: 600;
}
.foto-cuadro:hover .foto-overlay {
  opacity: 1;
}
.foto-error {
  color: #dc3545;
  font-size: 0.72rem;
  margin: 0;
  text-align: center;
}
.foto-hint {
  color: #aaa;
  font-size: 0.68rem;
  margin: 0;
  text-align: center;
}

/* Columna de campos */
.campos-col {
  display: flex;
  flex-direction: column;
  gap: 0;
}

/* Filas de campos */
.form-row-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-bottom: 14px;
}
.form-row-1 {
  margin-bottom: 14px;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.field-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #444;
}
.req {
  color: #dc3545;
}

.field-input {
  width: 100%;
  padding: 8px 12px;
  border: 1.5px solid #d8d8d8;
  border-radius: 8px;
  font-size: 0.88rem;
  color: #333;
  transition: border-color 0.2s, box-shadow 0.2s;
  outline: none;
  background: #fff;
}
.field-input:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.12);
}
.field-error {
  border-color: #dc3545 !important;
}
.error-msg {
  font-size: 0.71rem;
  color: #dc3545;
}

/* vue-select */
.custom-select {
  font-size: 0.88rem;
}
:deep(.vs__dropdown-toggle) {
  border: 1.5px solid #d8d8d8;
  border-radius: 8px;
  padding: 3px 8px;
  min-height: 38px;
}
:deep(.vs__dropdown-toggle:focus-within) {
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.12);
}
:deep(.vs__dropdown-menu) {
  max-height: 180px;
  overflow-y: auto;
}
.select-error :deep(.vs__dropdown-toggle) {
  border-color: #dc3545;
}

/* Redes */
.redes-section {
  margin-bottom: 20px;
}
.redes-titulo {
  font-size: 0.82rem;
  font-weight: 600;
  color: #444;
  margin-bottom: 10px;
}
.redes-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.red-field {
  display: flex;
  align-items: center;
  border: 1.5px solid #d8d8d8;
  border-radius: 8px;
  overflow: hidden;
  transition: border-color 0.2s;
}
.red-field:focus-within {
  border-color: #007bff;
}
.red-icon {
  padding: 0 10px;
  display: flex;
  align-items: center;
  border-right: 1px solid #e8e8e8;
  background: #f8f8f8;
  flex-shrink: 0;
}
.red-icon img {
  width: 18px;
  height: 18px;
  object-fit: contain;
}
.red-input {
  border: none !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  padding: 8px 10px;
  font-size: 0.82rem;
}
.red-input:focus {
  box-shadow: none !important;
}

/* Navegación */
.nav-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}
.btn-nav {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 20px;
  border-radius: 8px;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  text-decoration: none;
  transition: all 0.2s ease;
}
.btn-primary-nav {
  background: #007bff;
  color: #fff;
}
.btn-primary-nav:hover {
  background: #0060df;
}
.btn-secondary-nav {
  background: #f0f0f0;
  color: #555;
}
.btn-secondary-nav:hover {
  background: #e0e0e0;
}

/* Responsive */
@media (max-width: 640px) {
  .foto-campos-row {
    grid-template-columns: 1fr;
  }
  .foto-col {
    flex-direction: row;
    align-items: flex-start;
    gap: 14px;
  }
  .foto-cuadro {
    width: 100px;
    height: 100px;
    border-radius: 10px;
    flex-shrink: 0;
  }
  .form-row-2 {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  .redes-grid {
    grid-template-columns: 1fr;
  }
}
</style>
